/** Actual source transferred from standalone-search f83fe2d1 via Registrar V2. Native lifecycle adaptation is documented in TRANSFER.json. */
import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { X as Xmark } from "lucide-react";

import { cn } from "@/lib/utils";
import { resolveTenantTheme } from "@/lib/theme-presets";
import { VisuallyHidden } from "./visually-hidden";
import { ExternalOverlayBranch } from "./external-overlay-branch";
import { externalOverlayIsOpen } from "@/lib/external-overlay";

/**
 * MD3 Side Sheet Component
 *
 * Specs applied:
 * - Scrim: 32% black (MD3 standard)
 * - Corner radius: 16px (MD3 Large)
 * - Entry: 300ms emphasized-decelerate
 * - Exit: 200ms emphasized-accelerate
 * - Elevation: Level 1 dual-shadow
 * - Width: Consumer-controlled via className
 */

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

/** MD3 Drag Handle - 32x4dp capsule with 48dp touch target */
function SheetDragHandle({ className }: { className?: string }) {
  return (
    <div
      data-slot="sheet-drag-handle"
      className={cn(
        "mx-auto my-4 h-1 w-8 shrink-0 rounded-full bg-border-strong",
        "",
        // 48dp minimum touch target via padding
        "py-3 -my-2",
        className,
      )}
      aria-hidden="true"
    />
  );
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        // MD3 scrim: 32% black, not 50%
        "fixed inset-0 z-50 bg-foreground/[0.32]",
        // MD3 motion: 300ms fade-in, 200ms fade-out
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "duration-[var(--motion-slow)] motion-reduce:duration-0",
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  showDragHandle = false,
  showCloseButton = true,
  closeButtonLabel = "Close",
  hideDefaultTitle = false,
  onInteractOutside,
  onEscapeKeyDown,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  /** Show MD3 drag handle (recommended for bottom sheets) */
  showDragHandle?: boolean;
  /** Show close button (set false for MD3-pure tap-to-dismiss) */
  showCloseButton?: boolean;
  /** Accessible label and native tooltip for the close button. */
  closeButtonLabel?: string;
  /** Hide the built-in VisuallyHidden title (set true when consumer provides its own SheetTitle) */
  hideDefaultTitle?: boolean;
}) {
  const anchor = React.useRef<HTMLSpanElement>(null);
  const [preset, setPreset] = React.useState<string>();
  React.useLayoutEffect(() => {
    const value = anchor.current?.closest("[data-theme-preset]")?.getAttribute("data-theme-preset")
      ?? document.documentElement.getAttribute("data-theme-preset");
    setPreset(resolveTenantTheme(value) ?? undefined);
  }, []);
  const isHorizontal = side === "left" || side === "right";
  const isVertical = side === "top" || side === "bottom";

  return (
    <>
    <span ref={anchor} hidden data-sheet-anchor="" />
    <SheetPortal>
      <SheetOverlay data-theme-preset={preset} />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-theme-preset={preset}
        aria-describedby={undefined}
        className={cn(
          // Base: MD3 surface with Level 1 elevation
          "bg-card fixed z-50 flex min-h-0 max-w-full flex-col border border-border",
          // MD3 Level 1 dual-shadow
          "shadow-[var(--elevation-overlay)]",
          // MD3 motion: 300ms entry (emphasized-decel), 200ms exit (emphasized-accel)
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "duration-[var(--motion-slow)] motion-reduce:duration-0",
          // MD3 emphasized easing via CSS (cubic-bezier)
          "ease-[var(--ease-standard)]",
          "",
          // Side-specific styles
          side === "right" && [
            "inset-y-0 right-0 h-full",
            "rounded-l-xl", // MD3 Large radius (16px) on inside edge
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
          ],
          side === "left" && [
            "inset-y-0 left-0 h-full",
            "rounded-r-xl",
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
          ],
          side === "top" && [
            "inset-x-0 top-0",
            "max-h-[85vh]",
            "rounded-b-xl",
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
          ],
          side === "bottom" && [
            "inset-x-0 bottom-0",
            "max-h-[85vh]",
            "rounded-t-xl",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          ],
          className,
        )}
        {...props}
        onInteractOutside={(event) => {
          if (externalOverlayIsOpen()) event.preventDefault();
          else onInteractOutside?.(event);
        }}
        onEscapeKeyDown={(event) => {
          if (externalOverlayIsOpen()) event.preventDefault();
          else onEscapeKeyDown?.(event);
        }}
      >
        <ExternalOverlayBranch />
        {!hideDefaultTitle && (
          <VisuallyHidden asChild>
            <SheetPrimitive.Title>Sheet</SheetPrimitive.Title>
          </VisuallyHidden>
        )}

        {/* MD3 drag handle for vertical sheets */}
        {showDragHandle && isVertical && <SheetDragHandle />}

        {children}

        {/* Close button - can be hidden for MD3-pure tap-scrim-to-dismiss */}
        {showCloseButton && (
          <SheetPrimitive.Close
            aria-label={closeButtonLabel}
            title={closeButtonLabel}
            className={cn(
              "absolute rounded-full p-2",
              "bg-well hover:bg-muted",
              "text-muted-foreground hover:text-foreground",
              "transition-colors duration-200",
              "focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden",
              "disabled:pointer-events-none",
              // Position based on side
              isHorizontal && "top-4 right-4",
              side === "top" && "top-4 right-4",
              side === "bottom" && "top-4 right-4",
            )}
          >
            <Xmark className="size-4" />
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
    </>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        // MD3 padding: 16dp horizontal, 24dp top for header prominence
        "flex flex-col gap-1 px-4 pt-6 pb-4",
        className,
      )}
      {...props}
    />
  );
}

function SheetBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-body"
      className={cn(
        // MD3 content padding: 16dp horizontal
        "flex-1 overflow-y-auto px-4 py-2",
        className,
      )}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        // MD3 footer: 16dp padding, gap for action buttons
        "mt-auto flex flex-col gap-2 p-4",
        // Subtle top border for separation
        "border-t border-rule",
        className,
      )}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        // MD3 Title Large: 22px, line-height 28px, weight 400
        "text-[22px] leading-7 font-normal text-foreground",
        // Tracking for MD3 display type
        "tracking-tight",
        className,
      )}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn(
        // MD3 Body Medium: 14px, line-height 20px
        "text-ui leading-5 text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetDragHandle,
  VisuallyHidden,
};
